import React, { useMemo, useCallback, useState, useEffect } from 'react';
import './EventCard.css';
import { useNavigate } from 'react-router-dom';

/**
 * EventCard - appears only inside CourseCardPage full screen view.
 * Shows event core info + embedded Readings & Assignments rollup.
 */
export default function EventCard({ event, assignments = [], onZoom, pickEventDate }) {
  const navigate = useNavigate();
  const evId = event.eventId || event.id;

  const start = useMemo(()=> pickEventDate ? pickEventDate(event) : (event.startTime || event.startDate || event.startDatetime || event.startDateTime || event.eventDate || event.date), [event, pickEventDate]);

  const formatDate = useCallback((dStr)=>{
    if(!dStr) return ''; try { const d = new Date(dStr); if(isNaN(d)) return ''; return d.toLocaleDateString(undefined,{month:'short', day:'numeric'});} catch { return ''; }
  },[]);
  const formatTime = useCallback((dStr)=>{
    if(!dStr) return ''; try { const d = new Date(dStr); if(isNaN(d)) return ''; return d.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'});} catch { return ''; }
  },[]);

  const dateStr = formatDate(start);
  const timeStr = formatTime(start);

  // Responsive assignment list length based on viewport height so header/meta always visible
  const [assignLimit, setAssignLimit] = useState(6);
  useEffect(() => {
    function calcLimit(h) {
      if (h < 520) return 2;
      if (h < 600) return 3;
      if (h < 680) return 4;
      if (h < 760) return 5;
      return 6;
    }
    const apply = () => setAssignLimit(calcLimit(window.innerHeight || 0));
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  const limitedAssignments = assignments.slice(0, assignLimit);
  const remaining = assignments.length - limitedAssignments.length;

  return (
    <div className="eventcard" data-id={evId}>
      <div className="eventcard-head" onClick={()=>navigate(`/event/${evId}`)} role="button" tabIndex={0} onKeyDown={(e)=>{ if(e.key==='Enter') navigate(`/event/${evId}`); }}>
        <h3 className="eventcard-title" title={event.title || event.name}>{event.title || event.name || 'Event'}</h3>
        <div className="eventcard-meta">
          {dateStr && <span>{dateStr}</span>}
          {timeStr && <span>{timeStr}</span>}
          {event.location && <span>{event.location}</span>}
          {event.eventTypeName && <span>{event.eventTypeName}</span>}
        </div>
      </div>
      <div className="eventcard-desc">{(event.description || event.details || event.summary || '').slice(0,160) || 'No description'}</div>
      <div className="eventcard-section-label">Readings & Assignments</div>
      <div className="eventcard-assignments">
        {assignments.length === 0 && <div className="eventcard-empty">None</div>}
        {limitedAssignments.map(a => {
          const id = a.assignmentId || a.id;
          const title = a.title || a.name || 'Assignment';
          return (
            <div key={id} className="eventcard-asn" title={title} onClick={()=> navigate(`/assignment/${id}`)}>
              <span className="eventcard-asn-dot" />
              <span className="eventcard-asn-title">{title}</span>
            </div>
          );
        })}
        {remaining > 0 && <div className="eventcard-more">+{remaining} more</div>}
      </div>
      <div className="eventcard-footer">
        <button type="button" className="eventcard-zoom-btn" onClick={()=> onZoom && onZoom(evId)} title="Zoom Event">
          <span className="icon" aria-hidden="true">⤢</span> Zoom
        </button>
      </div>
    </div>
  );
}
